import { useMemo } from 'react';

import { uniq } from 'lodash-es';
import { isAddress } from 'viem/utils';
import { gql } from '@soundxyz/gql-string';
import { SUPER_MINTER_V2_ADDRESS } from '@soundxyz/tiered-sdk-viem/contract/edition-v2/abi/super-minter-v2';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';

import { useAuth } from '@context/auth/base';
import { type MintReferral, MintReferralType } from '@context/mintReferral';
import { ParamsStore, useParams } from '@context/params';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, ReleaseReferrableInfoFragmentDoc } from '@graphql/reactQuery';
import { exhaustiveCheck } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleaseReferrableInfo on Release {
    id
    publicAffiliateFeeBPS
    isFirstPhaseCompleted
    samAddress
    artist {
      id
      user {
        id
        publicAddress
      }
    }
    credits {
      id
      publicAddress
    }

    listing {
      __typename
      ... on TieredEditionListing {
        id
        schedules {
          id
          minterAddress
        }
      }
    }
  }
`);

const AUTH_RESTRICTED_REFERRALS = process.env.NEXT_PUBLIC_AUTH_RESTRICTED_REFERRALS === 'true';

const emptyList: [] = [];

export function useCheckReferrableRelease({
  release,
}: {
  release: FragmentType<ReleaseReferrableInfoFragmentDoc> | null | undefined;
}) {
  const { authUserAddress = null } = useAuth();
  const releaseInfo = getFragment(ReleaseReferrableInfoFragmentDoc, release);

  const isAlwaysReferrable =
    releaseInfo?.listing?.__typename === 'TieredEditionListing' &&
    releaseInfo.listing.schedules.some(
      s => s.minterAddress.toLowerCase() === SUPER_MINTER_V2_ADDRESS.toLowerCase(),
    );
  const isReleaseReferrable =
    isAlwaysReferrable ||
    (!!releaseInfo?.publicAffiliateFeeBPS &&
      (!!releaseInfo.samAddress || !releaseInfo.isFirstPhaseCompleted));

  const { referral: referralParamAddressRaw } = useParams();

  // It's technically possible to refer a purchase to your own address, which means a discount since you could
  // recover the fee back to you, issue inherent with open referral systems, but we can at least prevent
  // this loophole from being as easy as putting your own address as referral address
  // by ignoring if that's the case. We only want this extra check for production
  // since for development it makes testing easier
  const referralParamAddress = referralParamAddressRaw?.toLowerCase();
  const restrictedReferralParamAddress = useMemo(() => {
    return AUTH_RESTRICTED_REFERRALS && authUserAddress === referralParamAddress
      ? [authUserAddress]
      : emptyList;
  }, [authUserAddress, referralParamAddress]);

  /**
   * Referral addresses that can't be added into the collect contract call
   */
  const unreferrableCollectAddresses = useMemo<string[]>(() => {
    if (!AUTH_RESTRICTED_REFERRALS) return emptyList;

    if (!releaseInfo) return restrictedReferralParamAddress;

    /**
     * We restrict so that artists or people within credits can't refer the release
     */
    return uniq([
      ...restrictedReferralParamAddress,
      ...releaseInfo.artist.user.publicAddress,
      ...releaseInfo.credits.map(v => v.publicAddress),
    ]);
  }, [releaseInfo, restrictedReferralParamAddress]);

  /**
   * Is the authenticated user allowed to add referral address to the release
   */
  const isAuthShareReferrable = useMemo(() => {
    // User is not authenticated, let's assume you can share with auth
    if (!authUserAddress) return true;

    // If restriction is not on place, return true early
    if (!AUTH_RESTRICTED_REFERRALS) return true;

    // If no release yet, assume you can
    if (!releaseInfo) return true;

    // An artist can't share with referral it's own release
    if (releaseInfo.artist.user.publicAddress === authUserAddress) return false;

    // If the authenticated user is within credits, no referrals
    for (const { publicAddress: creditPublicAddress } of releaseInfo.credits) {
      if (creditPublicAddress === authUserAddress) return false;
    }

    return true;
  }, [authUserAddress, releaseInfo]);

  const getAffiliate = useStableCallback((referral: MintReferral) => {
    let referralAddress = ParamsStore.referral?.toLowerCase();
    if (!referralAddress && referral) {
      const { referralData, referralType } = referral;
      if (referralType === MintReferralType.Playlist) {
        referralAddress = referralData.playlistUserAddress;
      } else if (referralType === MintReferralType.Post) {
        referralAddress = referralData.postUserAddress;
      } else {
        exhaustiveCheck(referralType);
      }
    }

    const affiliateAddress: string | undefined =
      referralAddress && isAddress(referralAddress) ? referralAddress : undefined;

    const affiliate =
      affiliateAddress && unreferrableCollectAddresses.includes(affiliateAddress)
        ? undefined
        : affiliateAddress;

    return affiliate;
  });

  return {
    isReleaseReferrable,
    isAuthShareReferrable,
    unreferrableCollectAddresses,
    isShareReferrable: isReleaseReferrable && isAuthShareReferrable,
    getAffiliate,
  };
}
